import { Controller } from 'stimulus'
import parseFloatString from 'helpers/parse-float-string'
import roundNumber from 'helpers/round-number'
import formatCurrency from 'helpers/new-format-currency'

import * as MortMath from 'modules/mort-math'

/**
 * Manages js for the home affordability page (StimulusJS controller)
 *
 * @class AffordabilityController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'byIncomeToggler',
    'byPaymentToggler',
    'dtiCopy',
    'dtiSlider',
    'downPayment',
    'downPaymentPercent',
    'expandTitle',
    'hoaDues',
    'homeInsurance',
    'householdAnnualIncome',
    'includesPMI',
    'interestRate',
    'loanTerm',
    'loanValue',
    'maximumMonthlyPayment',
    'monthlyDebt',
    'monthlyPayment',
    'monthlyPaymentSlider',
    'paymentInput',
    'propertyTax',
    'propertyTaxPercent'
  ]

  /**
   * @function Controllers.AffordabilityController.initialize
   * @memberof Controllers
   * @description Called on page load. Call the updateResultValues function
   *
   * @return {undefined} manipulates DOM
   */
  initialize() {
    this.updateResultValues()
  }

  /**
   * @function Controllers.AffordabilityController.updateResultValues
   * @memberof Controllers
   * @description Called whenever a value is changed, updates results
   *
   * @return {undefined} manipulates DOM
   */
  updateResultValues() {
    const data = this._prepareFormValues()
    if (data.loanLength === 0 || data.interestRate === 0) {
      this._zeroDisplayValues()
      return
    }

    this._setMonthlyPayment(data)
    data.loanAmount = roundNumber(MortMath.loanAmount(data), 0)
    this._setDownpaymentInputs(data)
    const pmiMultiplier = this._calculatePmi(data)
    const totalLoan = (data.loanAmount + data.downPayment) * pmiMultiplier || 0
    this.loanValueTarget.value = formatCurrency(totalLoan > 0 ? totalLoan : 0)

    this._updateTaxes(data)
  }

  /**
   * @function Controllers.AffordabilityController.toggleExpandTitle
   * @memberof Controllers
   * @description Changes from simple to advanced view
   *
   * @return {undefined} manipulates DOM
   */
  toggleExpandTitle() {
    const titles = this.expandTitleTargets
    titles.forEach(title => {
      title.classList.toggle('accordion__block--expanded')
      title.classList.toggle('accordion__block')
    })
  }

  /**
   * @function Controllers.AffordabilityController.toggleMonthyPayment
   * @memberof Controllers
   * @description Changes from income to payment view
   *
   * @return {undefined} manipulates DOM
   */
  toggleMonthyPayment() {
    const paymentInputs = this.paymentInputTargets
    paymentInputs.forEach(input => {
      input.classList.toggle('accordion__block--expanded')
      input.classList.toggle('accordion__block')
    })
  }

  /**
   * @function Controllers.AffordabilityController.maybeHighDti
   * @memberof Controllers
   * @description Changes the slider and copy classes and innerHTML when payment would be high dti
   *
   * @return {undefined} manipulates DOM
  */
  maybeHighDti() {
    if (this.dtiSliderTarget.value >= 43) {
      this._swapToRed()
    } else if (this.dtiSliderTarget.value >= 37) {
      this._swapToOrange()
    } else {
      this._swapToGreen()
    }
  }

  /**
  * @function Controllers.AffordabilityController._swapToRed
  * @memberof Controllers
  * @description Changes color and copy for aggressive dti
  *
  * @return {undefined} manipulates DOM
  */
  _swapToRed() {
    this.dtiSliderTarget.classList.add('form__range-input--red')
    this.dtiSliderTarget.classList.remove('form__range-input--orange', 'form__range-input--green')
    this.dtiCopyTarget.classList.add('mortgage-calculator__dti-copy--red')
    this.dtiCopyTarget.classList.remove('mortgage-calculator__dti-copy--green', 'mortgage-calculator__dti-copy--orange')
    this.dtiCopyTarget.innerHTML = 'be aggressive for'
  }

  /**
   * @function Controllers.AffordabilityController._swapToOrange
   * @memberof Controllers
   * @description Changes color and copy for stretch dti
   *
   * @return {undefined} manipulates DOM
  */
  _swapToOrange() {
    this.dtiSliderTarget.classList.add('form__range-input--orange')
    this.dtiSliderTarget.classList.remove('form__range-input--red', 'form__range-input--green')
    this.dtiCopyTarget.classList.add('mortgage-calculator__dti-copy--orange')
    this.dtiCopyTarget.classList.remove('mortgage-calculator__dti-copy--green', 'mortgage-calculator__dti-copy--red')
    this.dtiCopyTarget.innerHTML = 'stretch'
  }

  /**
   * @function Controllers.AffordabilityController._swapToGreen
   * @memberof Controllers
   * @description Changes color and copy for comfortable dti
   *
   * @return {undefined} manipulates DOM
   */
  _swapToGreen() {
    this.dtiSliderTarget.classList.add('form__range-input--green')
    this.dtiSliderTarget.classList.remove('form__range-input--red', 'form__range-input--orange')
    this.dtiCopyTarget.classList.add('mortgage-calculator__dti-copy--green')
    this.dtiCopyTarget.classList.remove('mortgage-calculator__dti-copy--orange', 'mortgage-calculator__dti-copy--red')
    this.dtiCopyTarget.innerHTML = 'fit comfortably within'
  }

  /**
   * @function Controllers.AffordabilityController._maybeUseDollarAmountTaxes
   * @memberof Controllers
   * @description Calculate taxes if the user manipulates the property tax field
   * @note The event variable used here is global to the controller
   *
   * @return {float} returns monthly taxes
   */
  _maybeUseDollarAmountTaxes() {
    return event && event.currentTarget.name === 'property-tax' ? (event.currentTarget.value / 12) || 0 : null
  }

  /**
   * @function Controllers.AffordabilityController._setMonthlyPayment
   * @memberof Controllers
   * @description Determines if monthly payment should be calculated by payment or by income
   *
   * @param {Object} data - Data object with form values
   *
   * @return {undefined} manipulates DOM
   */
  _setMonthlyPayment(data) {
    this.byIncomeTogglerTarget.checked ? this._monthlyPaymentsByIncome(data) : this._monthlyPaymentsByPayment(data)
  }

  /**
   * @function Controllers.AffordabilityController._monthlyPaymentsByIncome
   * @memberof Controllers
   * @description sets data value for monthly payment
   *
   * @param {Object} data - Data object with form values
   *
   * @return {undefined} manipulates DOM
   */
  _monthlyPaymentsByIncome(data) {
    const monthlyPayment = MortMath.monthlyPaymentByIncome(data)
    const displayMonthlyPayment = formatCurrency(monthlyPayment > 0 ? monthlyPayment : 0)
    this.monthlyPaymentTarget.value = displayMonthlyPayment
    data.monthlyPayment = monthlyPayment
  }

  /**
   * @function Controllers.AffordabilityController._monthlyPaymentsByPayment
   * @memberof Controllers
   * @description sets data value for monthly payment
   *
   * @param {Object} data - Data object with form values
   *
   * @return {undefined} manipulates DOM
   */
  _monthlyPaymentsByPayment(data) {
    this._displayMonthlyPaymentsByPayment(data)
    data.monthlyPayment = MortMath.monthlyPaymentByPayment(data)
  }

  /**
   * @function Controllers.AffordabilityController._displayMonthlyPaymentsByPayment
   * @memberof Controllers
   * @description sets the values for the montly payment elements and data object
   * @note The event variable used here is global to the controller
   *
   * @param {Object} data - Data object with form values
   *
   * @return {undefined} manipulates DOM
   */
  _displayMonthlyPaymentsByPayment(data) {
    if (event && event.currentTarget.name === 'maximum-monthly-payment') {
      const max = parseFloatString(this.maximumMonthlyPaymentTarget.value) * 2
      const min = parseFloatString(this.maximumMonthlyPaymentTarget.value) / 2
      this.monthlyPaymentSliderTarget.setAttribute('max', max)
      this.monthlyPaymentSliderTarget.setAttribute('min', min)
    }

    let maximumPayment
    if (event && event.currentTarget.name === 'monthly_payment_slider') {
      maximumPayment = event.currentTarget.value
      this.maximumMonthlyPaymentTarget.value = parseFloatString(maximumPayment)
    } else {
      maximumPayment = this.maximumMonthlyPaymentTarget.value
      this.monthlyPaymentSliderTarget.value = parseFloatString(maximumPayment)
    }

    data.maximumMonthlyPayment = parseFloatString(maximumPayment)
    this.monthlyPaymentTarget.value = formatCurrency(maximumPayment)
  }

  /**
   * @function Controllers.AffordabilityController._setDownpaymentInputs
   * @memberof Controllers
   * @description sets the values for the down payment and down payment percent elements
   * @note The event variable used here is global to the controller
   *
   * @param {Object} data - Data object with form values
   *
   * @return {undefined} manipulates DOM
   */
  _setDownpaymentInputs(data) {
    if (event && event.currentTarget.name == 'down-payment-percent') {
      data.downPayment = roundNumber(data.loanAmount * data.downPaymentPercent)
      this.downPaymentTarget.value = roundNumber(data.downPayment, 0)
    } else {
      const downPaymentPercent = (data.downPayment / data.loanAmount)
      data.downPaymentPercent = downPaymentPercent > 0 ? downPaymentPercent : 0
      this.downPaymentPercentTarget.value = roundNumber(data.downPaymentPercent * 100, 2)
    }
  }

  /**
   * @function Controllers.AffordabilityController._calculatePmi
   * @memberof Controllers
   * @description returns the pmi multiplier
   *
   * @param {Object} data - Data object with form values
   *
   * @return {float} pmi multiplier
   */
  _calculatePmi(data) {
    return this.includesPMITarget.checked ? MortMath.pmiMultiplier(data.downPaymentPercent) : 1
  }

  /**
   * @function Controllers.AffordabilityController._updateTaxes
   * @memberof Controllers
   * @description updates the tax fields once the percentage can be known (ie. after there is a loanAmount)
   * @note The event variable used here is global to the controller
   *
   * @param {Object} data - Data object with form values
   *
   * @return {undefined} manipulates DOM
   */
  _updateTaxes(data) {
    if (event && event.currentTarget.name === 'property-tax') {
      const taxPercent = roundNumber((data.propertyTax / data.loanAmount) * 100, 2)
      this.propertyTaxPercentTarget.value = taxPercent > 0 ? taxPercent : 0
    } else {
      const tax = roundNumber((this.propertyTaxPercentTarget.value * data.loanAmount) / 100, 0)
      this.propertyTaxTarget.value = tax > 0 ? tax : 0
    }
  }

  /**
   * @function Controllers.AffordabilityController._zeroDisplayValues
   * @memberof Controllers
   * @description
   * @note
   *
   *
   * @return {undefined}
   */
  _zeroDisplayValues() {
    this.monthlyPaymentTarget.value = formatCurrency(0)
    this.loanValueTarget.value = formatCurrency(0)
  }

  /**
   * @function Controllers.AffordabilityController._prepareFormValues
   * @memberof Controllers
   * @description creates a data object with form values
   *
   * @return {object} data object with form values
   */
  _prepareFormValues() {
    const downPayment = parseFloatString(this.downPaymentTarget.value)
    const downPaymentPercent = this.downPaymentPercentTarget.value / 100
    const dtiPercent = this.dtiSliderTarget.value / 100
    const householdAnnualIncome = parseFloatString(this.householdAnnualIncomeTarget.value)
    const includesPMI = this.includesPMITarget.value
    const interestRate = this.interestRateTarget.value / 100
    const loanLength = this.loanTermTarget.value * 12
    const maximumMonthlyPayment = parseFloatString(this.maximumMonthlyPaymentTarget.value) || 0
    const monthlyDebt = parseFloatString(this.monthlyDebtTarget.value) || 0
    const monthlyHoa = parseFloatString(this.hoaDuesTarget.value) || 0
    const monthlyIncome = householdAnnualIncome / 12
    const monthlyInsurance = (parseFloatString(this.homeInsuranceTarget.value) / 12) || 0
    // const monthlyTaxes = this._maybeUseDollarAmountTaxes()
    const monthlyTaxRate = (this.propertyTaxPercentTarget.value / 100 / 12) || 0
    const propertyTax = parseFloatString(this.propertyTaxTarget.value) || 0

    return {
      downPayment,
      downPaymentPercent,
      dtiPercent,
      householdAnnualIncome,
      includesPMI,
      interestRate,
      loanLength,
      maximumMonthlyPayment,
      monthlyDebt,
      monthlyHoa,
      monthlyIncome,
      monthlyInsurance,
      // monthlyTaxRate,
      monthlyTaxRate,
      propertyTax
    }
  }
}
